'use client';

import { CONTENT_EVENTS } from '@repo/analytics';
import type { IMovieRatingData, IRateMovieV1Body } from '@repo/sdk';
import { Link } from '@repo/web-chrome/components/link';
import { trackEvent } from '@repo/web-chrome/lib/analytics';
import { Bookmark, BookmarkCheck, Eye, LogIn, RefreshCw } from 'lucide-react';
import type React from 'react';
import { RatingThumbs } from './rating-thumbs';
import { TrackingPill } from './tracking-pill';
import { TrackingSkeleton } from './tracking-skeleton';
import type { ITrackingStrings } from './tracking-strings';
import { useCollectionTracking } from './use-collection-tracking';
import { WatchedControl } from './watched-control';

interface ICollectionTrackingProps {
  collectionId: number;
  strings: ITrackingStrings;
}

interface ITrackingRowProps {
  collectionId: number;
  data: IMovieRatingData;
  pending: boolean;
  strings: ITrackingStrings;
  save: (update: IRateMovieV1Body) => Promise<void>;
}

const ROW_CLASS = 'mt-4 flex min-h-9 flex-wrap items-center gap-2';

function SignedOutRow({ label }: { label: string }): React.JSX.Element {
  return (
    <Link
      href="/login"
      className="inline-flex h-9 items-center gap-1.5 rounded-full border border-border bg-card px-3.5 text-sm font-medium text-muted-foreground transition outline-none hover:bg-muted hover:text-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
    >
      <LogIn className="size-4" aria-hidden />
      {label}
    </Link>
  );
}

function UnwatchedRow({
  collectionId,
  data,
  pending,
  strings,
  save,
}: ITrackingRowProps): React.JSX.Element {
  function markWatched(): void {
    trackEvent(CONTENT_EVENTS.movieWatched, { collectionId });
    void save({ watched: true });
  }

  function toggleWatchlist(): void {
    const next = !data.wantsToWatch;
    trackEvent(next ? CONTENT_EVENTS.movieWatchlistAdded : CONTENT_EVENTS.movieWatchlistRemoved, {
      collectionId,
    });
    void save({ wantsToWatch: next });
  }

  return (
    <>
      <TrackingPill
        icon={Eye}
        label={strings.markWatched}
        disabled={pending}
        onClick={markWatched}
      />
      <TrackingPill
        icon={data.wantsToWatch ? BookmarkCheck : Bookmark}
        label={data.wantsToWatch ? strings.watchlistOn : strings.watchlistAdd}
        active={data.wantsToWatch}
        ariaPressed={data.wantsToWatch}
        disabled={pending}
        onClick={toggleWatchlist}
      />
    </>
  );
}

function WatchedRow(props: ITrackingRowProps): React.JSX.Element {
  return (
    <>
      <WatchedControl {...props} />
      <RatingThumbs
        collectionId={props.collectionId}
        rating={props.data.rating}
        pending={props.pending}
        strings={props.strings}
        save={props.save}
      />
    </>
  );
}

export function CollectionTracking({
  collectionId,
  strings,
}: ICollectionTrackingProps): React.JSX.Element {
  const { status, data, pending, save, retry } = useCollectionTracking({
    collectionId,
    saveErrorMessage: strings.saveError,
  });
  const rowProps = { collectionId, data, pending, strings, save };

  return (
    <div className={ROW_CLASS}>
      {status === 'loading' && <TrackingSkeleton />}
      {status === 'signedOut' && <SignedOutRow label={strings.signIn} />}
      {status === 'error' && (
        <>
          <span className="text-sm text-destructive">{strings.loadError}</span>
          <TrackingPill icon={RefreshCw} label={strings.retry} onClick={retry} />
        </>
      )}
      {status === 'ready' &&
        (data.watched ? <WatchedRow {...rowProps} /> : <UnwatchedRow {...rowProps} />)}
    </div>
  );
}
